import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { usePoll } from "@redotech/react-util/poll";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoPlatformAuthRpcClientContext } from "@redotech/redo-merchant-app-common/platform/redo-platform-rpc-client-provider";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import {
  IncidentProduct,
  IncidentSeverity,
} from "@redotech/redo-model/incidents/incident";
import {
  ConversionPermissions,
  ImsPermissions,
  MarketingPermissions,
  OrderManagementPermissions,
  ReturnsAndClaimsPermissions,
  SupportPermissions,
} from "@redotech/redo-model/permissions";
import { ManagedUserRoles } from "@redotech/redo-model/roles";
import {
  NotificationBannerItem,
  NotificationType,
  RedoNotificationBanner,
} from "@redotech/redo-web/arbiter-components/notification/redo-notification-banner";
import { memo, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import { ConversionSuiteStatusesContext } from "../../../redo-suite/conversion-cloud";
import { InventoryManagementSuiteStatusesContext } from "../../../redo-suite/inventory-cloud";
import { MarketingCloudStatusesContext } from "../../../redo-suite/marketing-cloud";
import { OrderManagementSuiteStatusesContext } from "../../../redo-suite/order-management-cloud";
import { SupportSuiteStatusesContext } from "../../../redo-suite/support-cloud";

export const IncidentsBanner = memo(function IncidentsBanner() {
  const platformRpc = useRequiredContext(RedoPlatformAuthRpcClientContext);

  const [dismissedIds, setDismissedIds] = useLocalStorage<string[]>(
    "incident-banners-dismissed",
    [],
  );

  const orderManagementStatuses = useContext(
    OrderManagementSuiteStatusesContext,
  );
  const marketingStatuses = useRequiredContext(MarketingCloudStatusesContext);
  const { supportEnabled } = useRequiredContext(SupportSuiteStatusesContext);
  const { checkoutOptimizationEnabled } = useRequiredContext(
    ConversionSuiteStatusesContext,
  );
  const { imsEnabled } = useRequiredContext(
    InventoryManagementSuiteStatusesContext,
  );

  const canViewSupport = useHasPermission(SupportPermissions.VIEW_SUPPORT);
  const canViewInventoryManagement = useHasPermission(
    ImsPermissions.ACCESS_INVENTORY,
  );
  const canViewReturns = useHasPermission(
    ReturnsAndClaimsPermissions.VIEW_RETURN,
    ReturnsAndClaimsPermissions.MANAGE_PACKAGE_PROTECTION_CLAIM,
    ReturnsAndClaimsPermissions.MANAGE_WARRANTY,
    OrderManagementPermissions.VIEW_ORDER,
  );
  const canViewMarketing = useHasPermission(
    MarketingPermissions.VIEW_MARKETING,
  );
  const canViewConversion = useHasPermission(
    ConversionPermissions.ACCESS_CONVERSION,
  );
  const canViewOMS = useHasPermission(
    OrderManagementPermissions.VIEW_ORDER,
    OrderManagementPermissions.SCAN_AND_VERIFY,
    OrderManagementPermissions.MANAGE_SHIPMENTS,
  );
  const user = useContext(UserContext);
  const isAdmin =
    user?.managedUserRoles?.includes(ManagedUserRoles.ADMIN) ?? false;

  const location = useLocation();

  const incidentsLoad = usePoll(
    async (signal) => {
      const result = await platformRpc.getCurrentIncidents(null, { signal });
      return result;
    },
    Temporal.Duration.from({ minutes: 5 }),
    [platformRpc],
  );

  const onDismiss = useHandler((id: string) => {
    setDismissedIds((prev) => {
      const set = new Set(prev);
      set.add(id);
      const activeIncidents = (incidentsLoad.value || []).map(
        (incident) => incident._id,
      );
      return Array.from(set).filter((dismissedId) =>
        activeIncidents.includes(dismissedId),
      );
    });
  });

  const filteredIncidents: NotificationBannerItem[] = useMemo(() => {
    const getTypes = (item: {
      type?: IncidentProduct;
      types?: IncidentProduct[];
    }): IncidentProduct[] =>
      Array.isArray(item.types) ? item.types : item.type ? [item.type] : [];

    const userCanSeeProduct = (
      item: { routeScoped?: boolean },
      product: IncidentProduct,
    ): boolean => {
      switch (product) {
        case IncidentProduct.ALL:
          return true;
        case IncidentProduct.MARKETING:
          return marketingStatuses.marketingCloudEnabled && canViewMarketing;
        case IncidentProduct.RECOMMERCE:
          return imsEnabled && canViewInventoryManagement;
        case IncidentProduct.CONVERSION:
          return checkoutOptimizationEnabled && canViewConversion;
        case IncidentProduct.SUPPORT:
          return supportEnabled && canViewSupport;
        case IncidentProduct.RETURNS:
          return orderManagementStatuses.returnsEnabled && canViewReturns;
        case IncidentProduct.OMS:
          if (!orderManagementStatuses.omsEnabled || !canViewOMS) {
            return false;
          }
          if (item.routeScoped === false) {
            return true;
          }
          return location.pathname.includes(
            "/order-management/outbound-labels",
          );
      }
    };

    const items = (incidentsLoad.value || [])
      .filter((incident) => !dismissedIds.includes(incident._id))
      .filter((incident) => !incident.adminOnly || isAdmin)
      .filter((incident) => {
        const types = getTypes(incident);
        // Empty types = All of Redo: show to everyone
        if (types.length === 0) {
          return true;
        }
        // Show if user has access to ANY of the listed products (OR semantics)
        return types.some((product) => userCanSeeProduct(incident, product));
      });

    return items.map((item) => ({
      id: item._id,
      message: item.message,
      type: incidentSeverityToNotificationType[item.severity],
      actions: item.publicUrl
        ? [
            {
              actionType: "link",
              text: "Learn more",
              href: item.publicUrl,
              internal: false,
            },
          ]
        : [],
    }));
  }, [
    incidentsLoad.value,
    dismissedIds,
    marketingStatuses,
    canViewMarketing,
    imsEnabled,
    canViewInventoryManagement,
    checkoutOptimizationEnabled,
    canViewConversion,
    supportEnabled,
    canViewSupport,
    orderManagementStatuses,
    canViewReturns,
    canViewOMS,
    isAdmin,
    location.pathname,
  ]);

  return (
    <RedoNotificationBanner items={filteredIncidents} onDismiss={onDismiss} />
  );
});

const incidentSeverityToNotificationType: Record<
  IncidentSeverity,
  NotificationType
> = {
  [IncidentSeverity.ERROR]: "error",
  [IncidentSeverity.WARNING]: "warning",
  [IncidentSeverity.INFO]: "info",
};
